import { useMemo, useState } from 'react'
import React from 'react'

import { Check, Circle } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import { cn } from '@fortem-monorepo/js-utilities'
import { FormError, SearchSelect } from '@fortem-monorepo/react-ui'
import { CommandGroup, CommandItem, CommandSeparator } from '@fortem-monorepo/react-ui'
import { FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'
import { Misc } from '@fortem-monorepo/web-sdk'

export interface StrategyIdFormFieldProps {
  strategies?: Misc.GetStrategiesData
  loading?: boolean
}

export const StrategyIdFormField: React.FC<StrategyIdFormFieldProps> = ({
  strategies,
  loading,
}) => {
  const { control, setValue, watch } = useFormContext<{
    strategy_id: number
    strategy_type: string
  }>()
  const [open, setOpen] = useState(false)

  const selectedStrategyId = watch('strategy_id')

  const onSelect = (strategy: Misc.GetStrategiesData[number]) => {
    const { strategy_id, strategy_type } = strategy
    setValue('strategy_id', strategy_id, {
      shouldValidate: true,
    })
    setValue('strategy_type', strategy_type, { shouldValidate: true })
    setOpen(false)
  }

  const strategyGroups = useMemo(() => {
    type Strategy = Misc.GetStrategiesData[number]
    type StrategyGroups = Record<string, Strategy[]>

    const _groups: StrategyGroups = (strategies ?? []).reduce((prev, current) => {
      const _new = { ...prev }

      if (!_new[current.strategy_type]) {
        _new[current.strategy_type] = [current]
      } else {
        _new[current.strategy_type].push(current)
      }

      return _new
    }, {} as StrategyGroups)

    return _groups
  }, [strategies])

  return (
    <FormField
      name="strategy_id"
      control={control}
      render={({ field }) => {
        return (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>Strategy</FormLabel>
              <FormError />
            </div>
            <SearchSelect
              open={open}
              setOpen={setOpen}
              placeholder={'Select a Strategy'}
              loading={loading}
              value={field.value?.toString()}
              options={strategies?.map((item) => ({
                label: item.strategy_name,
                value: item.strategy_id?.toString(),
              }))}
            >
              {Object.entries(strategyGroups)?.map(([label, strategies], index) => {
                const isFirst = index === 0

                return (
                  <React.Fragment key={index}>
                    {!isFirst && <CommandSeparator />}
                    <CommandGroup heading={label}>
                      {strategies?.map((strategy) => {
                        const { strategy_name, strategy_id, active } = strategy
                        const is_selected = strategy_id == selectedStrategyId

                        return (
                          <CommandItem
                            value={strategy_name?.toString()}
                            onSelect={() => {
                              onSelect(strategy)
                            }}
                            disabled={is_selected}
                            key={strategy.strategy_id}
                          >
                            <div className={'flex items-center gap-2 justify-between w-full'}>
                              <div className={'flex items-center gap-1'}>
                                <Circle
                                  stroke={undefined}
                                  className={cn('size-3', {
                                    'fill-green-400/80': active,
                                    'fill-red-400/80': !active,
                                  })}
                                />
                                <span>{strategy_name}</span>
                              </div>
                              {selectedStrategyId === strategy_id && (
                                <Check className={cn('text-green-700 ml-auto h-4 w-4')} />
                              )}
                            </div>
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  </React.Fragment>
                )
              })}
            </SearchSelect>
          </FormItem>
        )
      }}
    />
  )
}
